import React, { useEffect, useState } from 'react';
import './mcp.css';
import axios from 'axios';
import { message } from 'antd';
import McpHeader from '../../../components/mcp/McpHeader';
import McpCRMAuth from './McpCRMAuth';
import McpIntegrations from './McpIntegrations';
import McpAddinsConfiguration from './McpAddinsConfiguration';
import { useAuth } from '../../../contexts/AuthContext';
import { authHeader } from '../../../utils/api';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
];

const SMTP_PROVIDERS = [
  { value: 'gmail', label: 'Gmail', host: 'smtp.gmail.com', port: 587, secure: false },
  { value: 'outlook', label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: 587, secure: false },
  { value: 'hotmail', label: 'Hotmail / Live', host: 'smtp.live.com', port: 587, secure: false },
  { value: 'yahoo', label: 'Yahoo Mail', host: 'smtp.mail.yahoo.com', port: 587, secure: false },
  { value: 'godaddy', label: 'GoDaddy Email', host: 'smtpout.secureserver.net', port: 465, secure: true },
  { value: 'hostinger', label: 'Hostinger Email', host: 'smtp.hostinger.com', port: 465, secure: true },
  { value: 'zoho', label: 'Zoho Mail', host: 'smtp.zoho.com', port: 587, secure: false },
  { value: 'sendgrid', label: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, secure: false },
  { value: 'mailgun', label: 'Mailgun', host: 'smtp.mailgun.org', port: 587, secure: false },
  { value: 'custom', label: 'Custom / Other', host: '', port: 587, secure: false },
];

const TABS = [
  { key: 'profile', label: 'Profile' },
  { key: 'security', label: 'Security & Login' },
  { key: 'email', label: 'Email Settings' },
  { key: 'geotab', label: 'GeoTab' },
  { key: 'crm', label: 'CRM Auth' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'addins', label: 'Add-Ins' },
  { key: 'billing', label: 'Billing & Plans' },
];

const DEFAULT_FORM = {
  fullName: '',
  email: '',
  phone: '',
  company: '',
  jobTitle: '',
  timezone: 'America/New_York',
  language: 'en',
  emailNotifications: true,
  marketingEmails: false,
};

const DEFAULT_SECURITY = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const DEFAULT_SMTP = {
  provider: 'custom',
  host: '',
  port: 587,
  user: '',
  pass: '',
  from_name: '',
  secure: false,
};

const DEFAULT_GEOTAB = {
  database: '',
  username: '',
  password: '',
  server: 'my.geotab.com',
  enabled: false,
};

const McpSettings = () => {
  const { currentUser, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [dataLoading, setDataLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [security, setSecurity] = useState(DEFAULT_SECURITY);
  const [smtp, setSmtp] = useState(DEFAULT_SMTP);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [geotab, setGeotab] = useState(DEFAULT_GEOTAB);

  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser?.id) return;
      setDataLoading(true);
      try {
        const response = await axios.get(`/api/users/${currentUser.id}`, { headers: authHeader() });
        const userData = response.data || {};
        setForm({
          fullName: userData.full_name || currentUser?.full_name || '',
          email: userData.email || currentUser?.email || '',
          phone: userData.phone || currentUser?.phone || '',
          company: userData.company || currentUser?.company || '',
          jobTitle: userData.job_title || userData.jobTitle || currentUser?.job_title || '',
          timezone: userData.timezone || currentUser?.timezone || 'America/New_York',
          language: userData.language || currentUser?.language || 'en',
          emailNotifications: userData.email_notifications ?? currentUser?.email_notifications ?? true,
          marketingEmails: userData.marketing_emails ?? currentUser?.marketing_emails ?? false,
        });
      } catch (error) {
        console.error('Failed to load profile:', error);
        message.error('Failed to load profile data');
      } finally {
        setDataLoading(false);
      }
    };
    loadProfile();
  }, [currentUser]);

  useEffect(() => {
    const loadSmtp = async () => {
      if (!currentUser?.id || activeTab !== 'email') return;
      try {
        const response = await axios.get('/api/clients/me/smtp-settings', { headers: authHeader() });
        const s = response.data || {};
        const provider = SMTP_PROVIDERS.find(p => p.host === s.host) || { value: 'custom' };
        setSmtp({
          provider: provider.value,
          host: s.host || '',
          port: s.port || 587,
          user: s.user || '',
          pass: '',
          from_name: s.from_name || '',
          secure: s.secure || false,
        });
        setSmtpConfigured(s.configured || false);
      } catch (error) {
        if (error.response?.status === 401) return;
        console.error('Failed to load SMTP settings:', error);
      }
    };
    loadSmtp();
  }, [activeTab, currentUser]);

  useEffect(() => {
    const loadGeotab = async () => {
      if (!currentUser?.id || activeTab !== 'geotab') return;
      try {
        const response = await axios.get('/api/clients/me/geotab-settings', { headers: authHeader() });
        const s = response.data || {};
        setGeotab({
          database: s.database || '',
          username: s.username || '',
          password: '',
          server: s.server || 'my.geotab.com',
          enabled: s.enabled ?? false,
        });
      } catch (error) {
        if (error.response?.status === 401) return;
        if (error.response?.status === 404) {
          setGeotab(DEFAULT_GEOTAB);
          return;
        }
        console.error('Failed to load GeoTab settings:', error);
      }
    };
    loadGeotab();
  }, [activeTab, currentUser]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!currentUser?.id) return;
    setSaving(true);
    try {
      const response = await axios.put(`/api/users/${currentUser.id}`, {
        full_name: form.fullName,
        email: form.email,
        phone: form.phone,
        company: form.company,
        job_title: form.jobTitle,
        timezone: form.timezone,
        language: form.language,
        email_notifications: form.emailNotifications,
        marketing_emails: form.marketingEmails,
      }, { headers: authHeader() });

      if (response.data) {
        updateUser(response.data);
        message.success('Profile updated successfully');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      message.error(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/') || !currentUser?.id) return;

    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await axios.post(`/api/users/${currentUser.id}/profile-picture`, { dataUrl }, { headers: authHeader() });
      if (response.data) {
        updateUser({ profile_picture_url: response.data.profile_picture_url });
        message.success('Profile picture updated');
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      message.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handleSecuritySave = async (e) => {
    e.preventDefault();
    if (!currentUser?.id) return;
    if (security.newPassword !== security.confirmPassword) {
      message.error('Passwords do not match');
      return;
    }
    if (security.newPassword.length < 8) {
      message.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`/api/users/${currentUser.id}/password`, {
        current_password: security.currentPassword,
        new_password: security.newPassword,
      }, { headers: authHeader() });
      message.success('Password updated successfully');
      setSecurity(DEFAULT_SECURITY);
    } catch (error) {
      console.error('Security update error:', error);
      message.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/api/clients/me/smtp-settings', {
        host: smtp.host,
        port: smtp.port,
        user: smtp.user,
        pass: smtp.pass,
        secure: smtp.secure,
        from_name: smtp.from_name,
      }, { headers: authHeader() });
      message.success('Email settings saved successfully');
      setSmtpConfigured(true);
    } catch (error) {
      console.error('SMTP save error:', error);
      message.error(error.response?.data?.error || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpTest = async () => {
    if (!smtp.host || !smtp.user) {
      message.warning('Fill in Host and Email before testing');
      return;
    }
    setSaving(true);
    try {
      await axios.put('/api/clients/me/smtp-settings', {
        host: smtp.host,
        port: smtp.port,
        user: smtp.user,
        pass: smtp.pass,
        secure: smtp.secure,
        from_name: smtp.from_name,
      }, { headers: authHeader() });
      const res = await axios.post('/api/email-campaigns/test-smtp', {}, { headers: authHeader() });
      message.success(res.data?.message || 'SMTP connection successful');
    } catch (error) {
      console.error('SMTP test error:', error);
      message.error(error.response?.data?.error || 'SMTP connection failed');
    } finally {
      setSaving(false);
    }
  };

  const handleGeotabSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put('/api/clients/me/geotab-settings', {
        database: geotab.database,
        username: geotab.username,
        password: geotab.password,
        server: geotab.server,
        enabled: geotab.enabled,
      }, { headers: authHeader() });
      message.success('GeoTab settings saved successfully');
    } catch (error) {
      console.error('GeoTab save error:', error);
      message.error(error.response?.data?.error || 'Failed to save GeoTab settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSmtpProviderChange = (value) => {
    const preset = SMTP_PROVIDERS.find(p => p.value === value);
    setSmtp(prev => ({
      ...prev,
      provider: value,
      host: preset?.host || '',
      port: preset?.port || 587,
      secure: preset?.secure || false,
    }));
  };

  const avatarUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const explicit = process.env.REACT_APP_API_URL;
    if (explicit) return explicit.replace(/\/api$/, '') + url;
    const host = window.location.hostname?.toLowerCase();
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
    return (isLocal ? 'http://localhost:5000' : 'https://api.pathmarkr.com') + url;
  };

  const initials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <div className="mcp-page" style={{ minHeight: '100vh' }}>
      <McpHeader title="Settings" />
      <main className="mx-auto w-full max-w-6xl p-6 md:p-8">
        <header className="mb-8">
          <h2 className="font-display text-2xl font-bold text-ink">Settings</h2>
          <p className="mt-1 text-sm text-ink-200">Manage your account and preferences.</p>
        </header>

        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                activeTab === t.key
                  ? 'bg-ink text-paper'
                  : 'text-ink-100 hover:bg-paper-200 hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'profile' && (
          <SettingsCard heading="Profile" description="Your personal and company information.">
            <form onSubmit={handleProfileSave} className="space-y-6">
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-ink text-2xl font-bold text-paper">
                  {currentUser?.profile_picture_url ? (
                    <img
                      src={avatarUrl(currentUser.profile_picture_url)}
                      alt="avatar"
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    initials(form.fullName)
                  )}
                </div>
                <div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                      disabled={uploading || dataLoading}
                    />
                    <span className="inline-flex items-center rounded-full border border-paper-300 bg-paper px-4 py-2 text-sm font-semibold text-ink transition hover:border-ink/30">
                      {uploading ? 'Uploading…' : 'Change Avatar'}
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" value={form.fullName} onChange={v => update('fullName', v)} />
                <Field label="Email" type="email" value={form.email} onChange={v => update('email', v)} />
                <Field label="Phone" value={form.phone} onChange={v => update('phone', v)} />
                <Field label="Company" value={form.company} onChange={v => update('company', v)} />
                <Field label="Job title" value={form.jobTitle} onChange={v => update('jobTitle', v)} />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-ink">Time zone</label>
                  <select
                    value={form.timezone}
                    onChange={e => update('timezone', e.target.value)}
                    className="mt-1.5 w-full rounded-2xl border border-paper-300 bg-paper-50 px-4 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
                  >
                    {TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-ink">Language</label>
                  <select
                    value={form.language}
                    onChange={e => update('language', e.target.value)}
                    className="mt-1.5 w-full rounded-2xl border border-paper-300 bg-paper-50 px-4 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
                  >
                    {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Toggle
                  label="Email notifications"
                  checked={form.emailNotifications}
                  onChange={() => update('emailNotifications', !form.emailNotifications)}
                />
                <Toggle
                  label="Marketing emails"
                  checked={form.marketingEmails}
                  onChange={() => update('marketingEmails', !form.marketingEmails)}
                />
              </div>

              <button
                type="submit"
                disabled={saving || dataLoading}
                className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-brand-dark disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          </SettingsCard>
        )}

        {activeTab === 'security' && (
          <div className="space-y-8">
            <SettingsCard heading="Change password" description="Update your account password.">
              <form onSubmit={handleSecuritySave} className="space-y-4">
                <Field label="Current password" type="password" value={security.currentPassword} onChange={v => setSecurity(prev => ({ ...prev, currentPassword: v }))} />
                <Field label="New password" type="password" value={security.newPassword} onChange={v => setSecurity(prev => ({ ...prev, newPassword: v }))} />
                <Field label="Confirm new password" type="password" value={security.confirmPassword} onChange={v => setSecurity(prev => ({ ...prev, confirmPassword: v }))} />
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-brand-dark disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Update password'}
                </button>
              </form>
            </SettingsCard>

            <SettingsCard heading="Two-Factor Authentication" description="Add an extra layer of security to your account.">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-ink">Status</div>
                  <div className="text-sm text-ink-200">Disabled</div>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center rounded-full bg-sun px-5 py-2.5 text-sm font-semibold text-ink transition hover:brightness-105"
                >
                  Enable 2FA
                </button>
              </div>
            </SettingsCard>

            <SettingsCard heading="Active sessions" description="Devices currently logged into your account.">
              <div className="flex items-center justify-between rounded-2xl border border-paper-300 bg-paper p-3">
                <div>
                  <div className="text-sm font-semibold text-ink">Current Session</div>
                  <div className="text-xs text-ink-200">Windows · Chrome · 192.168.1.100</div>
                  <div className="text-xs text-ink-200">Last active: Just now</div>
                </div>
                <span className="rounded-full bg-paper-200 px-3 py-1 text-xs font-semibold text-ink-100">
                  Current
                </span>
              </div>
            </SettingsCard>
          </div>
        )}

        {activeTab === 'email' && (
          <SettingsCard heading="Email Settings" description="Configure your own SMTP server for campaign emails.">
            <form onSubmit={handleSmtpSave} className="space-y-5">
              {smtpConfigured && (
                <div className="rounded-2xl border border-sage/40 bg-sage/10 p-3 text-sm font-semibold text-sage">
                  Your SMTP is configured — emails will be sent from your own email address.
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-ink">Email provider</label>
                <select
                  value={smtp.provider}
                  onChange={e => handleSmtpProviderChange(e.target.value)}
                  className="mt-1.5 w-full rounded-2xl border border-paper-300 bg-paper-50 px-4 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
                >
                  {SMTP_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Your email address" type="email" value={smtp.user} onChange={v => setSmtp(prev => ({ ...prev, user: v }))} />
                <Field label="Password / app password" type="password" value={smtp.pass} onChange={v => setSmtp(prev => ({ ...prev, pass: v }))} placeholder={smtpConfigured ? 'Leave blank to keep current' : ''} />
                <Field label="Sender display name" value={smtp.from_name} onChange={v => setSmtp(prev => ({ ...prev, from_name: v }))} />
              </div>

              <div className="border-t border-paper-300 pt-4">
                <div className="mb-3 text-sm font-semibold text-ink-200">Advanced (auto-filled by provider)</div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="SMTP host" value={smtp.host} onChange={v => setSmtp(prev => ({ ...prev, host: v }))} />
                  <Field label="Port" value={smtp.port} onChange={v => setSmtp(prev => ({ ...prev, port: Number(v) || 0 }))} />
                  <div>
                    <label className="text-sm font-semibold text-ink">Use SSL/TLS</label>
                    <select
                      value={smtp.secure ? 'true' : 'false'}
                      onChange={e => setSmtp(prev => ({ ...prev, secure: e.target.value === 'true' }))}
                      className="mt-1.5 w-full rounded-2xl border border-paper-300 bg-paper-50 px-4 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
                    >
                      <option value="false">No</option>
                      <option value="true">Yes (port 465)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-brand-dark disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Save settings'}
                </button>
                <button
                  type="button"
                  onClick={handleSmtpTest}
                  disabled={saving}
                  className="inline-flex items-center rounded-full border border-paper-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink transition hover:border-ink/30 disabled:opacity-60"
                >
                  Test connection
                </button>
              </div>
            </form>
          </SettingsCard>
        )}

        {activeTab === 'geotab' && (
          <SettingsCard heading="GeoTab Settings" description="Configure your GeoTab integration for GPS tracking and vehicle data.">
            <form onSubmit={handleGeotabSave} className="space-y-4">
              <Field label="Database name" value={geotab.database} onChange={v => setGeotab(prev => ({ ...prev, database: v }))} />
              <Field label="Username" value={geotab.username} onChange={v => setGeotab(prev => ({ ...prev, username: v }))} />
              <Field label="Password" type="password" value={geotab.password} onChange={v => setGeotab(prev => ({ ...prev, password: v }))} />
              <Field label="Server" value={geotab.server} onChange={v => setGeotab(prev => ({ ...prev, server: v }))} />
              <Toggle
                label="Enable GeoTab integration"
                checked={geotab.enabled}
                onChange={() => setGeotab(prev => ({ ...prev, enabled: !prev.enabled }))}
              />
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-brand-dark disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Save settings'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className="inline-flex items-center rounded-full border border-paper-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink transition hover:border-ink/30 disabled:opacity-60"
                >
                  Test connection
                </button>
              </div>
            </form>
          </SettingsCard>
        )}

        {activeTab === 'crm' && <McpCRMAuth />}

        {activeTab === 'integrations' && <McpIntegrations />}

        {activeTab === 'addins' && <McpAddinsConfiguration />}

        {activeTab === 'billing' && (
          <section className="mcp-card3d p-6">
            <div className="mb-3 text-3xl text-brand">ⓘ</div>
            <div className="font-bold text-ink mb-2">Your account is comped.</div>
            <p className="text-sm text-ink-200 m-0">Unlimited vehicles, full access, no billing on this account. Nothing to manage here.</p>
          </section>
        )}
      </main>
    </div>
  );
};

function SettingsCard({ heading, description, children }) {
  return (
    <section className="rounded-2xl border border-paper-300 bg-paper-50 p-5">
      <div>
        <h3 className="font-display text-lg font-bold text-ink">{heading}</h3>
        <p className="mt-1 text-sm text-ink-200">{description}</p>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Field({ label, type = 'text', value, onChange, placeholder = '' }) {
  return (
    <div>
      <label className="text-sm font-semibold text-ink">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-2xl border border-paper-300 bg-paper-50 px-4 py-2.5 text-sm text-ink focus:border-brand focus:outline-none"
      />
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-paper-300 bg-paper px-3 py-2.5">
      <span className="text-sm font-semibold text-ink">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`relative h-7 w-12 rounded-full transition-colors ${checked ? 'bg-sun' : 'bg-paper-300'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 inline-block h-6 w-6 rounded-full bg-paper-50 shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-0'}`}
        />
      </button>
    </label>
  );
}

export default McpSettings;
